import { DetailsMenuItem, User } from 'shared';

/** Client employee access rights as returned by `/users/me` (`accessRights`). */
export const CrowndeskAccessRight = {
  BILLING: 'BILLING',
  ORDERS: 'ORDERS',
  CAN_SEE_PURCHASE_PRICE: 'CAN_SEE_PURCHASE_PRICE',
  IS_CROWNDESK_ADMIN: 'IS_CROWNDESK_ADMIN',
} as const;

export type CrowndeskAccessRight = (typeof CrowndeskAccessRight)[keyof typeof CrowndeskAccessRight];

/** Royal Crown staff use CrownDesk to log in as a client employee instead of as themselves. */
export function isRoyalCrownEmployee(user: User | null | undefined): boolean {
  return !!user?.person?.startsWith('/royal_crown_employees/');
}

/** Supplier employees (the suppliers' representatives) only get their own part of CrownDesk, not the client screens. */
export function isSupplierEmployee(user: User | null | undefined): boolean {
  return !!user?.person?.startsWith('/supplier_employees/');
}

/** The pages of the supplier employee part: the open orders of their clients and their own user settings. */
export const SUPPLIER_EMPLOYEE_HOME = '/backorders';
const SUPPLIER_EMPLOYEE_PATHS = [SUPPLIER_EMPLOYEE_HOME, '/users'];

export function isSupplierEmployeePage(url: string): boolean {
  const path = url.split(/[?#]/)[0];
  return SUPPLIER_EMPLOYEE_PATHS.some((page) => path === page || path.startsWith(`${page}/`));
}

/** A CrownDesk admin has every right. */
export function hasAccessRight(user: User | null | undefined, right?: string): boolean {
  if (!right) return true;
  const rights = user?.accessRights ?? [];
  return rights.includes(CrowndeskAccessRight.IS_CROWNDESK_ADMIN) || rights.includes(right);
}

/** Removes menu items the user has no access to, and dropdowns left without items. */
export function filterMenuByAccessRights(menu: DetailsMenuItem[], user: User | null | undefined): DetailsMenuItem[] {
  return menu
    .filter((item) => hasAccessRight(user, item.accessRight))
    .map((item) => (item.items ? { ...item, items: filterMenuByAccessRights(item.items, user) } : item))
    .filter((item) => !item.items || item.items.length > 0);
}
